"use client";

import React from "react";
import { Building2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SmartCompanyDisplay } from "@/components/shared/smart-display/SmartCompanyDisplay";
import {
  type CompanyRow,
  formatAddress,
  getCompanyIndustry,
  getCompanyPhone,
  getCompanyWebsite,
} from "./types";

interface CompanyDetailsCardProps {
  company: CompanyRow;
  resolvedId: string | null;
  embedded: boolean;
  headerTitle: string;
  onViewFullCompany: () => void;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-sm font-medium text-gray-500">{label}</label>
      {children}
    </div>
  );
}

export function CompanyDetailsCard({
  company,
  resolvedId,
  embedded,
  headerTitle,
  onViewFullCompany,
}: CompanyDetailsCardProps) {
  const address = formatAddress(company);
  const website = getCompanyWebsite(company);
  const phone = getCompanyPhone(company);
  const industry = getCompanyIndustry(company);

  return (
    <div className={embedded ? "space-y-3" : "space-y-4"}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Building2 className="w-5 h-5 text-gray-600 dark:text-gray-400 shrink-0" />
          <h3
            className={
              embedded
                ? "text-sm font-semibold text-gray-900 dark:text-gray-100 truncate"
                : "text-base md:text-lg font-semibold text-card-foreground truncate"
            }
          >
            {headerTitle}
          </h3>
        </div>
        {resolvedId ? (
          <Button variant="outline" size="sm" onClick={onViewFullCompany} className="gap-2 shrink-0 h-11 sm:h-8">
            <ExternalLink className="w-4 h-4" />
            View full company
          </Button>
        ) : null}
      </div>

      <Card className={embedded ? "border bg-white dark:bg-gray-900" : undefined}>
        <CardContent className={embedded ? "pt-4 pb-4" : "pt-6"}>
          <div className="space-y-4">
            <Field label="Company">
              <div className="mt-1">
                <SmartCompanyDisplay
                  companyId={resolvedId || ""}
                  companyData={company}
                  fallbackText={company.name || "Company"}
                  showIcon={true}
                  showPopover={!embedded}
                  className="text-sm"
                />
              </div>
            </Field>

            {company.name ? (
              <Field label="Name">
                <p className="text-sm text-card-foreground mt-1">{company.name}</p>
              </Field>
            ) : null}

            {address ? (
              <Field label="Address">
                <p className="text-sm text-card-foreground mt-1 whitespace-pre-wrap">{address}</p>
              </Field>
            ) : null}

            {phone ? (
              <Field label="Phone">
                <p className="text-sm text-card-foreground mt-1">{phone}</p>
              </Field>
            ) : null}

            {website ? (
              <Field label="Website">
                <p className="text-sm mt-1">
                  <a
                    href={website.startsWith("http") ? website : `https://${website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {website}
                  </a>
                </p>
              </Field>
            ) : null}

            {industry ? (
              <Field label="Industry">
                <p className="text-sm text-card-foreground mt-1">{industry}</p>
              </Field>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
